import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, delay, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { User } from '../../models/user.class';

import { translate } from '@ngneat/transloco';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import * as sharedActions from '../actions/shared.actions';
import { AlertError, AlertSuccess } from '../actions/shared.actions';
import * as userActions from '../actions/users.actions';
import {
    getCurrentOrganizationId$,
    getOrgUsers$,
    getSelectedOrgIdToAssignUsers$,
    getSessionState$,
    getUsersByPagination$,
    getUserSettingsState$,
    getUsersPaginationStatus$,
    getUsersSearchState$,
    has4ECAccess$,
} from '../selectors';
import { UsersState } from '../states/users.state';

@Injectable()
export class UserEffects {
    private isGetAllUsersInProgress = false;

    constructor(private store: Store<UsersState>, private actions$: Actions, private _api: ApiService) {}

    getUserSettings = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.GetUserSettings),
            mergeMap(_action => {
                let userSettings: UsersState['settings'];
                this.store
                    .pipe(select(getUserSettingsState$))
                    .pipe(take(1))
                    .subscribe(settings => (userSettings = settings));
                if (userSettings?.passwordPolicy !== undefined) {
                    return [];
                }
                return from(this._api.session.getUserMiscProps()).pipe(
                    map(response => userActions.SetUserSettings(response)),
                    catchError(errorResponse => of(sharedActions.AlertError({ message: errorResponse })))
                );
            })
        );
    });

    resetUserPassword$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.ResetUserPassword),
            mergeMap(action => {
                return this._api.user.resetUserPassword(action.payload).pipe(
                    map(response => {
                        return AlertSuccess({ message: response.msg });
                    }),
                    catchError(response => {
                        return of(AlertError({ message: response.msg }));
                    })
                );
            })
        );
    });

    updatePassword = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.UpdatePassword),
            mergeMap(action => {
                const data = {
                    oldPassword: action.oldPassword,
                    newPassword: action.newPassword,
                    confirmPassword: action.confirmPassword,
                };
                return from(this._api.session.updatePassword(data)).pipe(
                    map(response => {
                        this.store.dispatch(sharedActions.AlertSuccess({ message: response }));
                        return userActions.UpdatePasswordSuccessfull({ message: response });
                    }),
                    catchError(errorResponse => {
                        this.store.dispatch(userActions.UpdatePasswordFailed({ message: errorResponse }));
                        return of(sharedActions.AlertError({ message: errorResponse }));
                    })
                );
            })
        );
    });

    getUsers = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.SetUsersPagination, userActions.SetUsersSearch),
            delay(10),
            withLatestFrom(
                this.store.select(getCurrentOrganizationId$),
                this.store.select(getUsersSearchState$),
                this.store.select(getUsersPaginationStatus$),
                this.store.select(getUsersByPagination$)
            ),
            mergeMap(([action, currentOrganizationId, search, pagination, users]) => {
                const hasCount = 'count' in action && action.count !== 0;
                const validUsers = users?.filter(u => !!u) || [];
                const pageFilled = validUsers.length > 0 && validUsers.length >= pagination?.pageSize;
                if (hasCount && pageFilled) {
                    return [];
                }
                const pageNumber = pagination.index + 1;
                return from(
                    this._api.user.getUsersByPagination({
                        organizationId: currentOrganizationId,
                        pageNumber,
                        pageSize: pagination.pageSize,
                        criterias: search,
                    })
                ).pipe(
                    map(({ users, count }) => {
                        return userActions.SetUsers({
                            data: users as any as User[],
                            organizationId: currentOrganizationId,
                            count,
                            pageNumber,
                        });
                    }),
                    catchError(error => of(AlertError({ message: error.msg || 'Failed to get users' })))
                );
            })
        );
    });

    addUser = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.UpdateUser),
            delay(10),
            withLatestFrom(this.store.select(getCurrentOrganizationId$), this.store.select(has4ECAccess$)),
            mergeMap(([action, organizationId, hasAccess]) => {
                if (action.data.user?.id || action?.data?.id) {
                    return from(this._api.user.updateUserDetails(action.data)).pipe(
                        map((response: any) => {
                            const message = hasAccess
                                ? translate(`Your request has been placed, it will effect after admin's approval`)
                                : translate('User Updated Successfully');
                            this.store.dispatch(
                                AlertSuccess({
                                    message,
                                })
                            );
                            return userActions.SetUser({
                                user: response,
                                newUser: false,
                                organizationId,
                                message: action.userAction && message,
                            });
                        }),
                        catchError(error => of(AlertError({ message: error.msg || 'Failed to update users' })))
                    );
                }
                return from(this._api.user.addUser(action.data)).pipe(
                    map((response: any) => {
                        const message = hasAccess
                            ? translate(`Your request has been placed, it will effect after admin's approval`)
                            : translate('User added successfully');
                        action.userAction &&
                            this.store.dispatch(
                                AlertSuccess({
                                    message,
                                })
                            );
                        return userActions.SetUser({
                            user: response,
                            newUser: true,
                            organizationId,
                            message: action.userAction && message,
                        });
                    }),
                    catchError(error => of(AlertError({ message: error.msg || 'Failed to update users' })))
                );
            })
        );
    });

    getSessionUserDetails = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.GetSessionUserDetails),
            delay(10),
            withLatestFrom(this.store.select(getCurrentOrganizationId$)),
            mergeMap(([_action, organizationId]) => {
                return from(this._api.user.getCurrentUserDetails()).pipe(
                    map((response: any) => {
                        return userActions.SetUser({
                            user: response,
                            newUser: false,
                            organizationId,
                            message: undefined,
                        });
                    }),
                    catchError(error => of(AlertError({ message: error.msg || 'Failed to update users' })))
                );
            })
        );
    });

    getUserDetails = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.GetUserDetails),
            delay(10),
            withLatestFrom(this.store.select(getCurrentOrganizationId$)),
            mergeMap(([action, organizationId]) => {
                return from(this._api.user.getUserDetails(action.userId)).pipe(
                    map((response: any) => {
                        return userActions.SetUser({
                            user: response,
                            newUser: true,
                            organizationId,
                            message: undefined,
                        });
                    }),
                    catchError(error => of(AlertError({ message: error.msg || 'Failed to update users' })))
                );
            })
        );
    });

    UnlockPassword = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.UnlockPassword),
            mergeMap(action => {
                return this._api.user.unlockPassword({ userId: action.userId }).pipe(
                    map((response: any) => {
                        return AlertSuccess({
                            message: response?.msg || 'User unlocked successfully',
                        });
                    }),
                    catchError(error => of(AlertError({ message: error.msg || 'Failed to unlock user' })))
                );
            })
        );
    });

    GetOrgUsers = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.GetOrgUsers),
            withLatestFrom(
                this.store.select(getSelectedOrgIdToAssignUsers$),
                this.store.select(getOrgUsers$),
                this.store.select(getSessionState$)
            ),
            mergeMap(([_action, selectedOrgId, orgUsers, sessionState]) => {
                if (!sessionState) {
                    return [];
                }
                if (orgUsers?.length > 0) {
                    return of(
                        userActions.SetOrgUsers({
                            orgId: selectedOrgId,
                            users: CommonUtilsService.cloneObject(orgUsers),
                        })
                    );
                }
                if (!selectedOrgId) {
                    setTimeout(() => {
                        this.store.dispatch(userActions.GetOrgUsers());
                    }, 100);
                    return [];
                }
                return this._api.user.getOrgUsersObservable({ organizationId: selectedOrgId }).pipe(
                    map((res: any) => {
                        return userActions.SetOrgUsers({
                            orgId: selectedOrgId,
                            users: res?.items || [],
                        });
                    }),
                    catchError(error => of(AlertError({ message: error.msg })))
                );
            })
        );
    });

    GetAllUsers = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.GetAllUsers),
            mergeMap(_action => {
                if (this.isGetAllUsersInProgress) {
                    return [];
                }
                this.isGetAllUsersInProgress = true;
                return this._api.user.getUsersAsObservable().pipe(
                    map((response: User[]) => {
                        this.isGetAllUsersInProgress = false;
                        return userActions.SetAllUsers({ users: response });
                    }),
                    catchError(error => {
                        this.isGetAllUsersInProgress = false;
                        return of(AlertError({ message: error.msg || 'Failed to get all users' }));
                    })
                );
            })
        );
    });

    getProviderUsers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.GetProviderUsers),
            mergeMap(_action => {
                return this._api.user.getProviderUsers().pipe(
                    map((response: any) => {
                        return userActions.SetProviderUsers({ users: response?.response || response });
                    }),
                    catchError(error => of(AlertError({ message: error.msg || 'Failed to get provider users' })))
                );
            })
        );
    });

    saveTimeBoundUsers$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.SaveTimeBoundUsers),
            mergeMap(action => {
                return this._api.user.saveTimeBoundUsers(action.payload).pipe(
                    map((response: any) => {
                        this.store.dispatch(AlertSuccess({ message: 'Time Bound Users saved successfully' }));
                        this.store.dispatch(userActions.GetTimeBoundUsersList());
                        return userActions.SaveTimeBoundUsersSuccess({ response: response?.response || response });
                    }),
                    catchError(error => of(AlertError({ message: error.msg || 'Failed to save time bound users' })))
                );
            })
        );
    });

    getTimeBoundUsersList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.GetTimeBoundUsersList),
            mergeMap(_action => {
                return this._api.user.getTimeBoundUsersList().pipe(
                    map((response: any) => {
                        return userActions.SetTimeBoundUsersList({ users: response?.response || [] });
                    }),
                    catchError(error => of(AlertError({ message: error.msg || 'Failed to get time bound users' })))
                );
            })
        );
    });

    deleteTimeBoundUserRoles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(userActions.DeleteTimeBoundUserRoles),
            mergeMap(action => {
                return this._api.user.deleteTimeBoundUserRoles(action.payload).pipe(
                    map((response: any) => {
                        this.store.dispatch(AlertSuccess({ message: 'Roles un-assigned successfully' }));
                        this.store.dispatch(userActions.GetTimeBoundUsersList());
                        return userActions.DeleteTimeBoundUserRolesSuccess({ response: response?.response || response });
                    }),
                    catchError(error => of(AlertError({ message: error.msg || 'Failed to un-assign roles' })))
                );
            })
        );
    });
}
